import React from 'react';
import { FormTextInput } from 'routes/auth/style';
import Form from 'components/form';
import AuthContainer from 'routes/auth/components/container';
import Password from 'routes/auth/components/password';
import { useTranslation } from 'i18n';
import { useUserApi } from 'services/user-service';
import { useLocation } from 'react-router';
import { useLoggedUser } from 'state/user';
import { useHistory } from 'react-router-dom';
import fetchLoggedUser from 'graphql/user/queries/fetch-logged-user';
import { object, string } from 'yup';
import storage from 'services/storage';
import { toast } from 'services/toast';
import { InnerFormHolderTop } from 'routes/auth/login/style';
import { InnerFormHolderBottom } from 'routes/auth/components/container/style';
import { OptionText } from 'routes/auth/components/container/style';
import { wsLink } from 'services/apollo-client/links';

export default function Login() {
  const { t } = useTranslation();
  const [_, setLoggedUser] = useLoggedUser();

  const location = useLocation();
  const userApi = useUserApi();
  const history = useHistory();
  const urlSearchParams = new URLSearchParams(location.search);
  const verificationMessage = urlSearchParams.get('message') || '';

  const onSubmit = React.useCallback(
    async ({ email, password, rememberMe }) => {
      const response = await userApi.login(email, password);

      if (response.isSuccess) {
        const token = response.getField('data.token');
        const user = response.getField('data.user');

        setLoggedUser(user);

        if (rememberMe) {
          await storage.setToken(token);
        } else {
          await storage.setTokenInSession(token);
        }

        await fetchLoggedUser();
        //@ts-ignore
        wsLink.subscriptionClient.connect();

        return history.push('/');
      }

      toast.error(response.getErrorMessage());
    },
    [userApi, history],
  );

  React.useEffect(() => {
    if (verificationMessage) {
      toast.success(
        t(
          'Your email has been successfully verified. Please login with your credentials.',
        ),
      );

      history.push('/login');
    }
  }, []);

  return (
    <AuthContainer
      titleText={t('Log in to your CortLex Account')}
      ctaLabelText={t('Do you need a CortLex account?')}
      ctaButtonText={t('Sign up for free')}
      onCtaButtonClick={() => history.push('/register')}>
      <Form
        initialValues={{
          email: new URLSearchParams(location.search).get('email') || '',
          password: '',
        }}
        validationSchema={object().shape({
          email: string()
            .email(t('Invalid email format'))
            .required(t('Email is required')),
          password: string()
            .required(t('No password provided'))
            .min(8, t('Password is too short - should be 8 chars minimum.')),
        })}
        onSubmit={onSubmit}
        submitOnEnter>
        {(form) => (
          <>
            <InnerFormHolderTop>
              <FormTextInput
                type="email"
                name="email"
                placeholder={t('E-Mail')}
              />
              <Password name="password" placeholder={t('Password')} />
              <Form.Button
                label={t('Forgot password?')}
                onClick={() => history.push('/forgot-password')}
                link
              />
            </InnerFormHolderTop>
            <InnerFormHolderBottom>
              <Form.Option
                label={
                  <OptionText>
                    {t('Remember my login on this computer')}
                  </OptionText>
                }
                name="rememberMe"
              />
              <Form.SubmitButton
                label={t('Login')}
                onClick={form.handleSubmit}
                loading
              />
            </InnerFormHolderBottom>
          </>
        )}
      </Form>
    </AuthContainer>
  );
}
